import { useQuery } from "@tanstack/react-query";
import { fetchAccountProfile } from "../services/account/accountProfileApi";
import type { AccountProfileResponse } from "../services/account/accountProfile.types";
import { readStoredUserKey } from "../services/auth/persistLoginSession";
import { getApiErrorStatus } from "../services/apiErrors";
import { queryKeys } from "../services/queryKeys";

const FIVE_MINUTES_MS = 5 * 60 * 1000;

/** 4xx (signed out, no such user) won't change on retry. */
const retryServerErrors = (failureCount: number, error: unknown): boolean => {
  const status = getApiErrorStatus(error);
  return (status === null || status >= 500) && failureCount < 2;
};

/**
 * The one GET /auth/me read for the signed-in user. Name, photo and the superadmin flag all
 * select from it, so they share a single request and refresh together when the profile changes.
 */
export const useOwnProfileQuery = <T = AccountProfileResponse>(
  select?: (profile: AccountProfileResponse) => T,
) =>
  useQuery({
    queryKey: queryKeys.auth.profile(readStoredUserKey()),
    queryFn: fetchAccountProfile,
    enabled: Boolean(readStoredUserKey()),
    staleTime: FIVE_MINUTES_MS,
    retry: retryServerErrors,
    select,
  });
